import * as THREE from "three";
import { mergeGeometries } from "three/addons/utils/BufferGeometryUtils.js";
import type {
  EnemyState,
  GameEvent,
  GameSnapshot,
  PickupState,
  ProjectileRenderMode,
  ProjectileState,
  RenderDiagnostics,
  VfxDiagnostics,
} from "../core/types";
import { SeededRandom } from "../core/random";

interface PulseEffect {
  mesh: THREE.Mesh;
  age: number;
  duration: number;
  maxScale: number;
}

interface SparkParticle {
  position: THREE.Vector3;
  velocity: THREE.Vector3;
  age: number;
  duration: number;
  color: number;
}

type ProjectileVariant = ProjectileState["variant"];

const PROJECTILE_VARIANTS: ProjectileVariant[] = ["torpedo", "surge", "plasma", "void"];
const MAX_PROJECTILES = 256;
const MAX_SPARKS = 260;

interface GameRendererOptions {
  projectileMode?: ProjectileRenderMode;
}

const COLORS = {
  abyss: 0x082225,
  deep: 0x0a2829,
  floor: 0x1b4f49,
  mint: 0x58e0c3,
  coral: 0xff6b5f,
  amber: 0xffd166,
  bone: 0xd6e7e2,
  ink: 0x091c20,
};

export class GameRenderer {
  private readonly renderer: THREE.WebGLRenderer;
  private readonly scene = new THREE.Scene();
  private readonly camera = new THREE.PerspectiveCamera(52, 1, 0.1, 120);
  private readonly clock = new THREE.Clock();
  private readonly random: SeededRandom;
  private readonly player: THREE.Group;
  private readonly relay: THREE.Group;
  private readonly targetReticle: THREE.Group;
  private readonly impactLight = new THREE.PointLight(COLORS.amber, 0, 12, 2);
  private readonly enemies = new Map<number, THREE.Object3D>();
  private readonly projectileMode: ProjectileRenderMode;
  private readonly projectileMeshes: Record<ProjectileVariant, THREE.InstancedMesh> | null;
  private readonly legacyProjectiles = new Map<number, THREE.Group>();
  private readonly sharedProjectileParts = new Map<ProjectileVariant, THREE.BufferGeometry[]>();
  private readonly projectileMaterials = new Map<ProjectileVariant, THREE.MeshBasicMaterial>();
  private readonly projectileTransform = new THREE.Object3D();
  private readonly pickups = new Map<number, THREE.Object3D>();
  private readonly pulses: PulseEffect[] = [];
  private readonly sparkGeometry = new THREE.TetrahedronGeometry(0.09, 0);
  private readonly sparkMaterial = new THREE.MeshBasicMaterial({ vertexColors: true });
  private readonly sparkMesh: THREE.InstancedMesh;
  private readonly sparkPool: SparkParticle[] = Array.from(
    { length: MAX_SPARKS },
    () => ({
      position: new THREE.Vector3(),
      velocity: new THREE.Vector3(),
      age: 0,
      duration: 0,
      color: COLORS.mint,
    }),
  );
  private readonly sparkTransform = new THREE.Object3D();
  private readonly sparkColor = new THREE.Color();
  private readonly plankton: THREE.Points;
  private readonly bubbles: THREE.Points;
  private activeSparkCount = 0;
  private elapsed = 0;
  private cameraShake = 0;

  constructor(canvas: HTMLCanvasElement, seed: number, options: GameRendererOptions = {}) {
    this.random = new SeededRandom(seed ^ 0x9e3779b9);
    this.projectileMode = options.projectileMode ?? "final";
    this.renderer = new THREE.WebGLRenderer({
      canvas,
      antialias: false,
      alpha: false,
      powerPreference: "high-performance",
    });
    const renderScale = window.innerWidth >= 1_200 ? 0.75 : window.innerWidth <= 720 ? 1.25 : 1;
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, renderScale));
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.34;
    this.scene.background = new THREE.Color(COLORS.abyss);
    this.scene.fog = new THREE.FogExp2(COLORS.abyss, 0.021);

    this.camera.position.set(0, 23, 21);
    this.camera.lookAt(0, 0, 0);
    this.buildLighting();
    this.buildSeabed();
    this.relay = this.buildRelay();
    this.scene.add(this.relay);
    this.player = this.buildSubmarine();
    this.scene.add(this.player);
    this.targetReticle = this.buildTargetReticle();
    this.scene.add(this.targetReticle, this.impactLight);
    this.projectileMeshes =
      this.projectileMode === "final" ? this.buildProjectileMeshes() : null;
    if (this.projectileMeshes) {
      this.scene.add(...Object.values(this.projectileMeshes));
    }
    this.sparkMesh = new THREE.InstancedMesh(
      this.sparkGeometry,
      this.sparkMaterial,
      MAX_SPARKS,
    );
    this.sparkMesh.count = 0;
    this.sparkMesh.frustumCulled = false;
    this.sparkMesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    this.sparkMesh.name = "spark-particle-pool";
    this.scene.add(this.sparkMesh);
    this.plankton = this.buildParticles(620, 0.035, COLORS.mint, 34, 13);
    this.bubbles = this.buildParticles(120, 0.09, COLORS.bone, 26, 10);
    this.scene.add(this.plankton, this.bubbles);
    this.resize();
    if (this.projectileMeshes) this.prewarmProjectileShaders();
    window.addEventListener("resize", this.resize);
  }

  update(snapshot: GameSnapshot, events: GameEvent[]): void {
    const delta = Math.min(0.05, this.clock.getDelta());
    this.elapsed += delta;
    this.syncPlayer(snapshot);
    this.syncEntities(snapshot);
    this.handleEvents(events, snapshot);
    this.animateWorld(delta, snapshot);
  }

  render(): void {
    this.renderer.render(this.scene, this.camera);
  }

  diagnostics(): RenderDiagnostics {
    let lights = 0;
    let objects = 0;
    this.scene.traverse((object) => {
      objects += 1;
      if (object instanceof THREE.Light) lights += 1;
    });
    const { render, memory } = this.renderer.info;
    return {
      calls: render.calls,
      triangles: render.triangles,
      points: render.points,
      lines: render.lines,
      geometries: memory.geometries,
      textures: memory.textures,
      lights,
      objects,
    };
  }

  vfxDiagnostics(): VfxDiagnostics {
    return {
      sparkMode: "preallocated-instanced",
      sparkCapacity: MAX_SPARKS,
      activeSparks: this.activeSparkCount,
    };
  }

  private buildLighting(): void {
    const hemisphere = new THREE.HemisphereLight(0x9fffea, 0x061719, 1.75);
    const key = new THREE.DirectionalLight(0xb8fff1, 2.2);
    key.position.set(-8, 18, 10);
    const coral = new THREE.PointLight(COLORS.coral, 24, 18, 2);
    coral.position.set(0, 2.5, 0);
    this.scene.add(hemisphere, key, coral);
  }

  private buildSeabed(): void {
    const floorGeometry = new THREE.CircleGeometry(44, 64);
    const floorMaterial = new THREE.MeshStandardMaterial({
      color: COLORS.floor,
      roughness: 0.96,
      metalness: 0.03,
      flatShading: true,
    });
    const floor = new THREE.Mesh(floorGeometry, floorMaterial);
    floor.rotation.x = -Math.PI / 2;
    floor.position.y = -2.2;
    this.scene.add(floor);

    const boundaryMaterial = new THREE.MeshBasicMaterial({
      color: COLORS.mint,
      transparent: true,
      opacity: 0.16,
      side: THREE.DoubleSide,
    });
    for (const radius of [29.5, 30.2]) {
      const ring = new THREE.Mesh(
        new THREE.RingGeometry(radius, radius + 0.08, 96),
        boundaryMaterial.clone(),
      );
      ring.rotation.x = -Math.PI / 2;
      ring.position.y = -2.08;
      this.scene.add(ring);
    }

    const rockGeometry = new THREE.DodecahedronGeometry(1, 0);
    const rockMaterial = new THREE.MeshStandardMaterial({
      color: 0x224943,
      roughness: 0.95,
      flatShading: true,
    });
    const rocks = new THREE.InstancedMesh(rockGeometry, rockMaterial, 72);
    const matrix = new THREE.Matrix4();
    const rotation = new THREE.Quaternion();
    const scale = new THREE.Vector3();
    const position = new THREE.Vector3();
    for (let index = 0; index < 72; index += 1) {
      const angle = this.random.range(0, Math.PI * 2);
      const radius = this.random.range(8, 42);
      position.set(Math.sin(angle) * radius, -1.9, Math.cos(angle) * radius);
      rotation.setFromEuler(
        new THREE.Euler(
          this.random.range(0, Math.PI),
          this.random.range(0, Math.PI),
          this.random.range(0, Math.PI),
        ),
      );
      const size = this.random.range(0.35, 1.8);
      scale.set(size, size * this.random.range(0.35, 0.75), size);
      matrix.compose(position, rotation, scale);
      rocks.setMatrixAt(index, matrix);
    }
    this.scene.add(rocks);

    const kelpGeometry = new THREE.ConeGeometry(0.22, 2.8, 5);
    const kelpMaterial = new THREE.MeshStandardMaterial({
      color: 0x2c7460,
      roughness: 0.8,
      flatShading: true,
    });
    const kelp = new THREE.InstancedMesh(kelpGeometry, kelpMaterial, 100);
    for (let index = 0; index < 100; index += 1) {
      const angle = this.random.range(0, Math.PI * 2);
      const radius = this.random.range(11, 41);
      const height = this.random.range(0.5, 1.45);
      position.set(Math.sin(angle) * radius, -2.15 + 1.4 * height, Math.cos(angle) * radius);
      rotation.setFromEuler(new THREE.Euler(0, this.random.range(0, Math.PI), 0));
      scale.set(this.random.range(0.7, 1.2), height, this.random.range(0.7, 1.2));
      matrix.compose(position, rotation, scale);
      kelp.setMatrixAt(index, matrix);
    }
    this.scene.add(kelp);
  }

  private buildRelay(): THREE.Group {
    const group = new THREE.Group();
    const dark = new THREE.MeshStandardMaterial({
      color: COLORS.ink,
      metalness: 0.72,
      roughness: 0.34,
    });
    const glow = new THREE.MeshStandardMaterial({
      color: COLORS.coral,
      emissive: COLORS.coral,
      emissiveIntensity: 2.6,
      roughness: 0.25,
    });
    const base = new THREE.Mesh(new THREE.CylinderGeometry(1.7, 2.2, 0.8, 8), dark);
    base.position.y = -1.72;
    const mast = new THREE.Mesh(new THREE.CylinderGeometry(0.18, 0.3, 4.4, 8), dark);
    mast.position.y = 0.1;
    const signal = new THREE.Mesh(new THREE.OctahedronGeometry(0.55, 0), glow);
    signal.position.y = 2.3;
    const halo = new THREE.Mesh(
      new THREE.TorusGeometry(1.3, 0.05, 8, 48),
      new THREE.MeshBasicMaterial({
        color: COLORS.coral,
        transparent: true,
        opacity: 0.75,
      }),
    );
    halo.rotation.x = Math.PI / 2;
    halo.position.y = 1.9;
    group.add(base, mast, signal, halo);
    return group;
  }

  private buildSubmarine(): THREE.Group {
    const group = new THREE.Group();
    const hullMaterial = new THREE.MeshStandardMaterial({
      color: 0xe9c45e,
      metalness: 0.52,
      roughness: 0.28,
    });
    const darkMaterial = new THREE.MeshStandardMaterial({
      color: 0x102d32,
      metalness: 0.7,
      roughness: 0.3,
    });
    const glassMaterial = new THREE.MeshStandardMaterial({
      color: 0x9affed,
      emissive: 0x247f75,
      emissiveIntensity: 1.5,
      metalness: 0.3,
      roughness: 0.12,
    });
    const hull = new THREE.Mesh(new THREE.CapsuleGeometry(0.65, 2.6, 5, 12), hullMaterial);
    hull.rotation.x = Math.PI / 2;
    const cockpit = new THREE.Mesh(new THREE.SphereGeometry(0.52, 16, 10), glassMaterial);
    cockpit.scale.set(0.78, 0.62, 1.05);
    cockpit.position.set(0, 0.46, 0.35);
    const fin = new THREE.Mesh(new THREE.BoxGeometry(2.5, 0.08, 0.7), darkMaterial);
    fin.position.z = -0.65;
    const tail = new THREE.Mesh(new THREE.BoxGeometry(0.12, 1.05, 0.8), darkMaterial);
    tail.position.z = -1.38;
    const lampMaterial = new THREE.MeshBasicMaterial({ color: COLORS.mint });
    const lamp = new THREE.Mesh(new THREE.SphereGeometry(0.12, 8, 6), lampMaterial);
    lamp.position.set(0, 0.15, 1.75);
    const lampLight = new THREE.SpotLight(COLORS.mint, 38, 16, Math.PI / 7, 0.8, 1.3);
    lampLight.position.set(0, 0.2, 1.55);
    lampLight.target.position.set(0, -0.4, 8);
    const shield = new THREE.Mesh(
      new THREE.SphereGeometry(1.45, 16, 10),
      new THREE.MeshBasicMaterial({
        color: 0x8da9ff,
        transparent: true,
        opacity: 0.18,
        wireframe: true,
        depthWrite: false,
      }),
    );
    shield.name = "shield";
    shield.visible = false;
    shield.scale.set(1.1, 0.72, 1.65);
    const surgeAura = new THREE.Mesh(
      new THREE.TorusGeometry(1.35, 0.08, 6, 32),
      new THREE.MeshBasicMaterial({
        color: COLORS.amber,
        transparent: true,
        opacity: 0.82,
        depthWrite: false,
      }),
    );
    surgeAura.name = "surge-aura";
    surgeAura.rotation.x = Math.PI / 2;
    surgeAura.visible = false;
    group.add(
      hull,
      cockpit,
      fin,
      tail,
      lamp,
      lampLight,
      lampLight.target,
      shield,
      surgeAura,
    );
    group.position.y = 0.15;
    return group;
  }

  private buildTargetReticle(): THREE.Group {
    const group = new THREE.Group();
    const material = new THREE.MeshBasicMaterial({
      color: COLORS.amber,
      transparent: true,
      opacity: 0.84,
      depthWrite: false,
    });
    const ring = new THREE.Mesh(new THREE.TorusGeometry(1, 0.035, 5, 32), material);
    ring.rotation.x = Math.PI / 2;
    group.add(ring);
    for (let index = 0; index < 4; index += 1) {
      const bracket = new THREE.Mesh(new THREE.BoxGeometry(0.48, 0.035, 0.055), material);
      const angle = (index / 4) * Math.PI * 2;
      bracket.position.set(Math.cos(angle) * 1.16, 0, Math.sin(angle) * 1.16);
      bracket.rotation.y = -angle;
      group.add(bracket);
    }
    group.visible = false;
    group.position.y = 0.9;
    return group;
  }

  private buildParticles(
    count: number,
    size: number,
    color: number,
    radius: number,
    height: number,
  ): THREE.Points {
    const positions = new Float32Array(count * 3);
    for (let index = 0; index < count; index += 1) {
      const angle = this.random.range(0, Math.PI * 2);
      const distance = Math.sqrt(this.random.next()) * radius;
      positions[index * 3] = Math.sin(angle) * distance;
      positions[index * 3 + 1] = this.random.range(-2, height);
      positions[index * 3 + 2] = Math.cos(angle) * distance;
    }
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute("position", new THREE.BufferAttribute(positions, 3));
    const material = new THREE.PointsMaterial({
      color,
      size,
      transparent: true,
      opacity: 0.58,
      sizeAttenuation: true,
    });
    return new THREE.Points(geometry, material);
  }

  private syncPlayer(snapshot: GameSnapshot): void {
    const { player } = snapshot;
    this.player.position.x = player.position.x;
    this.player.position.z = player.position.z;
    this.player.rotation.y = player.heading;
    this.player.visible = snapshot.phase !== "menu" || Math.sin(this.elapsed * 0.6) > -2;
    this.player.position.y = 0.2 + Math.sin(this.elapsed * 1.8) * 0.1;

    const shield = this.player.getObjectByName("shield");
    if (shield) shield.visible = player.shield > 0;
    const surgeAura = this.player.getObjectByName("surge-aura");
    if (surgeAura) {
      surgeAura.visible = player.overdriveFor > 0;
      surgeAura.rotation.z += 0.035;
      surgeAura.scale.setScalar(1 + Math.sin(this.elapsed * 9) * 0.14);
    }

    const targetCamera = new THREE.Vector3(
      player.position.x + this.random.range(-this.cameraShake, this.cameraShake),
      22 + Math.min(5, Math.hypot(player.velocity.x, player.velocity.z)),
      player.position.z + 20 + this.random.range(-this.cameraShake, this.cameraShake),
    );
    this.camera.position.lerp(targetCamera, 0.035);
    this.camera.lookAt(
      player.position.x + this.random.range(-this.cameraShake, this.cameraShake) * 0.35,
      -0.2,
      player.position.z - 1.5,
    );
    this.updateTargetReticle(snapshot);
  }

  private updateTargetReticle(snapshot: GameSnapshot): void {
    let target: EnemyState | undefined;
    let closest = Number.POSITIVE_INFINITY;
    for (const enemy of snapshot.enemies) {
      const distance = Math.hypot(
        enemy.position.x - snapshot.player.position.x,
        enemy.position.z - snapshot.player.position.z,
      );
      if ((enemy.revealedFor > 0 || enemy.type === "leviathan" || distance < 6.5) && distance < closest) {
        target = enemy;
        closest = distance;
      }
    }
    this.targetReticle.visible = Boolean(target);
    if (!target) return;
    const scale = Math.max(0.75, target.radius * (target.type === "leviathan" ? 1.45 : 1.1));
    this.targetReticle.position.set(target.position.x, target.type === "ray" ? 1.15 : 0.85, target.position.z);
    this.targetReticle.scale.setScalar(scale);
    this.targetReticle.rotation.y += 0.018;
  }

  private syncEntities(snapshot: GameSnapshot): void {
    this.syncMap(
      snapshot.enemies,
      this.enemies,
      (enemy) => this.buildEnemy(enemy),
      (object, enemy) => this.updateEnemy(object, enemy, snapshot),
    );
    this.syncProjectiles(snapshot.projectiles);
    this.syncMap(
      snapshot.pickups,
      this.pickups,
      (pickup) => this.buildPickup(pickup),
      (object, pickup) => this.updatePickup(object, pickup),
    );
  }

  private syncMap<T extends { id: number }>(
    entities: T[],
    objects: Map<number, THREE.Object3D>,
    create: (entity: T) => THREE.Object3D,
    update: (object: THREE.Object3D, entity: T) => void,
  ): void {
    const active = new Set<number>();
    for (const entity of entities) {
      active.add(entity.id);
      let object = objects.get(entity.id);
      if (!object) {
        object = create(entity);
        objects.set(entity.id, object);
        this.scene.add(object);
      }
      update(object, entity);
    }
    for (const [id, object] of objects) {
      if (!active.has(id)) {
        this.scene.remove(object);
        objects.delete(id);
      }
    }
  }

  private buildEnemy(enemy: EnemyState): THREE.Object3D {
    const bodyMaterial = this.createEnemyMaterial(
      enemy.type === "leviathan"
        ? 0x56304f
        : enemy.type === "ray"
          ? 0x658fa4
          : enemy.type === "mine"
            ? 0xb83d36
            : 0xd35a50,
      enemy.type === "leviathan" ? 0x220d27 : enemy.type === "mine" ? 0x7d231c : 0x29100e,
      enemy.type === "mine" ? 1.2 : enemy.type === "leviathan" ? 0.9 : 0.45,
    );
    if (enemy.type === "leviathan") return this.buildLeviathan(bodyMaterial);
    if (enemy.type === "drifter") return this.buildDrifter(bodyMaterial);
    if (enemy.type === "stalker") return this.buildStalker(bodyMaterial);
    if (enemy.type === "mine") return this.buildMine(bodyMaterial);
    return this.buildRay(bodyMaterial);
  }

  private createEnemyMaterial(
    color: number,
    emissive: number,
    emissiveIntensity: number,
  ): THREE.MeshStandardMaterial {
    const material = new THREE.MeshStandardMaterial({
      color,
      emissive,
      emissiveIntensity,
      roughness: 0.5,
      flatShading: true,
      transparent: true,
    });
    material.userData.baseEmissive = emissive;
    material.userData.baseEmissiveIntensity = emissiveIntensity;
    return material;
  }

  private buildDrifter(material: THREE.MeshStandardMaterial): THREE.Group {
    const group = new THREE.Group();
    group.userData.enemyType = "drifter";
    const dome = new THREE.Mesh(new THREE.SphereGeometry(0.82, 12, 8), material);
    dome.scale.set(1, 0.7, 1);
    dome.position.y = 0.18;
    const coreMaterial = this.createEnemyMaterial(COLORS.amber, COLORS.amber, 2.4);
    const core = new THREE.Mesh(new THREE.OctahedronGeometry(0.28, 0), coreMaterial);
    core.position.y = 0.14;
    core.name = "enemy-core";
    const collar = new THREE.Mesh(
      new THREE.TorusGeometry(0.7, 0.08, 6, 20),
      this.createEnemyMaterial(0x5a1e2d, 0x24070f, 0.7),
    );
    collar.rotation.x = Math.PI / 2;
    collar.position.y = -0.22;
    group.add(dome, core, collar);
    for (let index = 0; index < 5; index += 1) {
      const angle = (index / 5) * Math.PI * 2;
      const curve = new THREE.CatmullRomCurve3([
        new THREE.Vector3(Math.cos(angle) * 0.5, -0.25, Math.sin(angle) * 0.5),
        new THREE.Vector3(Math.cos(angle + 0.22) * 0.7, -0.72, Math.sin(angle + 0.22) * 0.7),
        new THREE.Vector3(Math.cos(angle - 0.18) * 0.45, -1.18, Math.sin(angle - 0.18) * 0.45),
      ]);
      const tentacle = new THREE.Mesh(
        new THREE.TubeGeometry(curve, 7, 0.07, 5, false),
        material,
      );
      tentacle.name = "tentacle";
      group.add(tentacle);
    }
    return group;
  }

  private buildStalker(material: THREE.MeshStandardMaterial): THREE.Group {
    const group = new THREE.Group();
    group.userData.enemyType = "stalker";
    const body = new THREE.Mesh(new THREE.CapsuleGeometry(0.48, 1.35, 4, 8), material);
    body.rotation.x = Math.PI / 2;
    const nose = new THREE.Mesh(new THREE.ConeGeometry(0.46, 0.8, 8), material);
    nose.rotation.x = Math.PI / 2;
    nose.position.z = 1.45;
    const finMaterial = this.createEnemyMaterial(0x541e26, 0x1d080d, 0.55);
    const sideFin = new THREE.Mesh(new THREE.BoxGeometry(1.9, 0.1, 0.62), finMaterial);
    sideFin.position.z = -0.28;
    const tailFin = new THREE.Mesh(new THREE.BoxGeometry(0.1, 1.22, 0.6), finMaterial);
    tailFin.position.z = -1.18;
    group.add(body, nose, sideFin, tailFin);
    const eyeMaterial = this.createEnemyMaterial(COLORS.amber, COLORS.amber, 2.7);
    for (const side of [-1, 1]) {
      const eye = new THREE.Mesh(new THREE.SphereGeometry(0.1, 7, 5), eyeMaterial);
      eye.position.set(side * 0.3, 0.23, 1.35);
      group.add(eye);
    }
    return group;
  }

  private buildMine(material: THREE.MeshStandardMaterial): THREE.Group {
    const group = new THREE.Group();
    group.userData.enemyType = "mine";
    const core = new THREE.Mesh(new THREE.DodecahedronGeometry(0.72, 0), material);
    core.name = "enemy-core";
    group.add(core);
    const spikeMaterial = this.createEnemyMaterial(0x5b1a20, 0x28070a, 0.8);
    const directions = [
      new THREE.Vector3(1, 0, 0),
      new THREE.Vector3(-1, 0, 0),
      new THREE.Vector3(0, 1, 0),
      new THREE.Vector3(0, -1, 0),
      new THREE.Vector3(0, 0, 1),
      new THREE.Vector3(0, 0, -1),
      new THREE.Vector3(1, 0.35, 1).normalize(),
      new THREE.Vector3(-1, -0.35, -1).normalize(),
    ];
    for (const direction of directions) {
      const spike = new THREE.Mesh(new THREE.ConeGeometry(0.14, 0.85, 5), spikeMaterial);
      spike.position.copy(direction).multiplyScalar(0.93);
      spike.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), direction);
      group.add(spike);
    }
    const warningMaterial = new THREE.MeshBasicMaterial({
      color: COLORS.amber,
      transparent: true,
      opacity: 0.72,
      depthWrite: false,
    });
    for (const rotation of [0, Math.PI / 2]) {
      const ring = new THREE.Mesh(new THREE.TorusGeometry(1.08, 0.035, 5, 24), warningMaterial);
      ring.rotation.x = rotation;
      ring.name = "warning-ring";
      group.add(ring);
    }
    return group;
  }

  private buildRay(material: THREE.MeshStandardMaterial): THREE.Group {
    const group = new THREE.Group();
    group.userData.enemyType = "ray";
    const body = new THREE.Mesh(new THREE.SphereGeometry(0.72, 12, 8), material);
    body.scale.set(1.15, 0.3, 1.45);
    const wingMaterial = this.createEnemyMaterial(0x426e82, 0x10272d, 0.62);
    for (const side of [-1, 1]) {
      const wing = new THREE.Mesh(new THREE.SphereGeometry(0.82, 10, 6), wingMaterial);
      wing.scale.set(1.8, 0.16, 0.9);
      wing.position.set(side * 1.08, 0, -0.05);
      wing.rotation.z = side * 0.09;
      wing.name = "ray-wing";
      group.add(wing);
    }
    const tail = new THREE.Mesh(new THREE.CylinderGeometry(0.045, 0.11, 2.3, 6), wingMaterial);
    tail.rotation.x = Math.PI / 2;
    tail.position.z = -1.85;
    const core = new THREE.Mesh(
      new THREE.OctahedronGeometry(0.22, 0),
      this.createEnemyMaterial(COLORS.mint, COLORS.mint, 2.2),
    );
    core.position.set(0, 0.2, 0.42);
    core.name = "enemy-core";
    group.add(body, tail, core);
    return group;
  }

  private buildLeviathan(material: THREE.MeshStandardMaterial): THREE.Group {
    const group = new THREE.Group();
    const glowMaterial = new THREE.MeshStandardMaterial({
      color: COLORS.mint,
      emissive: COLORS.mint,
      emissiveIntensity: 2.7,
      roughness: 0.18,
    });
    const finMaterial = new THREE.MeshStandardMaterial({
      color: 0x311d3a,
      emissive: 0x120819,
      emissiveIntensity: 0.8,
      roughness: 0.68,
      flatShading: true,
    });
    for (let index = 0; index < 9; index += 1) {
      const segment = new THREE.Mesh(
        new THREE.IcosahedronGeometry(Math.max(0.7, 2.2 - index * 0.16), 1),
        material,
      );
      segment.scale.set(1.6, 0.65, 1);
      segment.position.z = -index * 1.25;
      segment.position.y = Math.sin(index * 0.7) * 0.28;
      group.add(segment);

      if (index < 7) {
        const spine = new THREE.Mesh(new THREE.ConeGeometry(0.28, 1.15, 5), glowMaterial);
        spine.position.set(0, 1.15 - index * 0.045, segment.position.z);
        group.add(spine);
      }
    }

    const head = new THREE.Mesh(new THREE.DodecahedronGeometry(1.8, 0), material);
    head.scale.set(1.35, 0.82, 1.5);
    head.position.z = 1.45;
    const mouth = new THREE.Mesh(
      new THREE.TorusGeometry(0.82, 0.16, 7, 16),
      new THREE.MeshStandardMaterial({
        color: COLORS.coral,
        emissive: COLORS.coral,
        emissiveIntensity: 2.2,
        roughness: 0.25,
      }),
    );
    mouth.rotation.x = Math.PI / 2;
    mouth.position.set(0, -0.08, 3.72);
    group.add(head, mouth);

    for (const side of [-1, 1]) {
      const fin = new THREE.Mesh(new THREE.ConeGeometry(1.15, 4.2, 3), finMaterial);
      fin.rotation.z = side * (Math.PI / 2);
      fin.rotation.y = Math.PI / 2;
      fin.position.set(side * 2.15, -0.15, -0.4);
      group.add(fin);

      for (let index = 0; index < 2; index += 1) {
        const curve = new THREE.CatmullRomCurve3([
          new THREE.Vector3(side * (1 + index * 0.35), -0.2, 1.2 - index * 0.4),
          new THREE.Vector3(side * (2.8 + index), -0.6, 1.8 - index),
          new THREE.Vector3(side * (4.2 + index * 0.8), -1.1, 0.3 - index * 1.7),
        ]);
        const tentacle = new THREE.Mesh(
          new THREE.TubeGeometry(curve, 16, 0.16 - index * 0.025, 6, false),
          finMaterial,
        );
        group.add(tentacle);
      }

      const eye = new THREE.Mesh(
        new THREE.SphereGeometry(0.28, 8, 6),
        glowMaterial,
      );
      eye.position.set(side * 1.22, 0.42, 2.45);
      group.add(eye);
    }
    group.scale.setScalar(1.1);
    return group;
  }

  private updateEnemy(
    object: THREE.Object3D,
    enemy: EnemyState,
    snapshot: GameSnapshot,
  ): void {
    const bob = Math.sin(this.elapsed * 2.7 + enemy.id) * 0.12;
    object.position.set(enemy.position.x, enemy.type === "ray" ? 0.8 + bob : -0.05 + bob, enemy.position.z);
    object.rotation.y = enemy.heading;
    const distanceToPlayer = Math.hypot(
      enemy.position.x - snapshot.player.position.x,
      enemy.position.z - snapshot.player.position.z,
    );
    const visible = enemy.revealedFor > 0 || enemy.type === "leviathan" || distanceToPlayer < 6.5;
    object.visible = visible;
    const baseScale = enemy.type === "leviathan" ? 1.1 : 1;
    object.scale.set(
      baseScale,
      baseScale * (1 + Math.sin(this.elapsed * 3 + enemy.id) * 0.035),
      baseScale,
    );
    const hit = Number(object.userData.hitUntil ?? 0) > this.elapsed;
    object.traverse((child) => {
      if (child instanceof THREE.Mesh && child.material instanceof THREE.MeshStandardMaterial) {
        const material = child.material;
        if (material.userData.baseEmissive === undefined) {
          material.userData.baseEmissive = material.emissive.getHex();
          material.userData.baseEmissiveIntensity = material.emissiveIntensity;
        }
        material.emissive.setHex(hit ? 0xffffff : Number(material.userData.baseEmissive));
        material.emissiveIntensity = hit
          ? 4.5
          : Number(material.userData.baseEmissiveIntensity);
        child.material.opacity = visible ? Math.min(1, enemy.revealedFor * 2 + 0.35) : 0;
      }
      if (child.name === "enemy-core") {
        child.scale.setScalar(1 + Math.sin(this.elapsed * 8 + enemy.id) * 0.22);
      }
      if (child.name === "warning-ring") {
        child.rotation.z += 0.025;
        child.scale.setScalar(1 + Math.sin(this.elapsed * 7 + enemy.id) * 0.1);
      }
      if (child.name === "ray-wing") {
        child.rotation.z = Math.sign(child.position.x) * (0.09 + Math.sin(this.elapsed * 5 + enemy.id) * 0.08);
      }
    });
  }

  private buildProjectileMeshes(): Record<ProjectileVariant, THREE.InstancedMesh> {
    const create = (variant: ProjectileVariant): THREE.InstancedMesh => {
      const mesh = new THREE.InstancedMesh(
        this.buildProjectileGeometry(variant),
        this.projectileMaterial(variant),
        MAX_PROJECTILES,
      );
      mesh.count = 0;
      mesh.frustumCulled = false;
      mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
      mesh.name = `projectiles-${variant}`;
      return mesh;
    };
    return {
      torpedo: create("torpedo"),
      surge: create("surge"),
      plasma: create("plasma"),
      void: create("void"),
    };
  }

  private prewarmProjectileShaders(): void {
    if (!this.projectileMeshes) return;
    const matrix = new THREE.Matrix4().makeTranslation(0, -100, 0);
    for (const mesh of Object.values(this.projectileMeshes)) {
      mesh.setMatrixAt(0, matrix);
      mesh.count = 1;
      mesh.instanceMatrix.needsUpdate = true;
    }
    this.renderer.compile(this.scene, this.camera);
    for (const mesh of Object.values(this.projectileMeshes)) mesh.count = 0;
  }

  private buildProjectileGeometry(variant: ProjectileVariant): THREE.BufferGeometry {
    const geometries = this.buildProjectileParts(variant);
    const normalized = geometries.map((geometry) =>
      geometry.index ? geometry.toNonIndexed() : geometry,
    );
    const geometry = mergeGeometries(normalized, false);
    if (!geometry) throw new Error(`Unable to merge ${variant} projectile geometry`);
    geometry.computeBoundingSphere();
    return geometry;
  }

  private buildProjectileParts(variant: ProjectileVariant): THREE.BufferGeometry[] {
    const geometries: THREE.BufferGeometry[] = [];
    if (variant === "torpedo" || variant === "surge") {
      const body = new THREE.CylinderGeometry(0.13, 0.18, 0.75, 7);
      body.rotateX(Math.PI / 2);
      const nose = new THREE.ConeGeometry(0.18, 0.28, 7);
      nose.rotateX(Math.PI / 2);
      nose.translate(0, 0, 0.5);
      const fin = new THREE.BoxGeometry(0.55, 0.055, 0.24);
      fin.translate(0, 0, -0.28);
      const trail = new THREE.ConeGeometry(0.11, 0.5, 7, 1, true);
      trail.rotateX(-Math.PI / 2);
      trail.translate(0, 0, -0.64);
      geometries.push(body, nose, fin, trail);
      if (variant === "surge") {
        const ring = new THREE.TorusGeometry(0.28, 0.025, 5, 14);
        ring.rotateX(Math.PI / 2);
        geometries.push(ring);
      }
    } else {
      const core = new THREE.OctahedronGeometry(0.3, 0);
      const trail = new THREE.ConeGeometry(0.18, 0.7, 7, 1, true);
      trail.rotateX(-Math.PI / 2);
      trail.translate(0, 0, -0.52);
      geometries.push(core, trail);
      for (const offset of [-0.18, 0.18]) {
        const ring = new THREE.TorusGeometry(0.42, 0.025, 5, 12);
        ring.rotateX(Math.PI / 2);
        ring.translate(0, 0, offset);
        geometries.push(ring);
      }
    }
    return geometries;
  }

  private projectileMaterial(variant: ProjectileVariant): THREE.MeshBasicMaterial {
    let material = this.projectileMaterials.get(variant);
    if (material) return material;
    const colors: Record<ProjectileVariant, number> = {
      torpedo: COLORS.mint,
      surge: COLORS.amber,
      plasma: COLORS.coral,
      void: 0xb574ff,
    };
    material = new THREE.MeshBasicMaterial({
      color: colors[variant],
      transparent: true,
      opacity: 0.9,
    });
    this.projectileMaterials.set(variant, material);
    return material;
  }

  private sharedParts(variant: ProjectileVariant): THREE.BufferGeometry[] {
    let geometries = this.sharedProjectileParts.get(variant);
    if (geometries) return geometries;
    geometries = this.buildProjectileParts(variant);
    this.sharedProjectileParts.set(variant, geometries);
    return geometries;
  }

  private buildLegacyProjectile(projectile: ProjectileState): THREE.Group {
    const group = new THREE.Group();
    const geometries =
      this.projectileMode === "shared"
        ? this.sharedParts(projectile.variant)
        : this.buildProjectileParts(projectile.variant);
    const material = this.projectileMaterial(projectile.variant);
    for (const geometry of geometries) {
      const mesh = new THREE.Mesh(geometry, material);
      mesh.frustumCulled = false;
      group.add(mesh);
    }
    if (this.projectileMode === "baseline") {
      const color =
        projectile.variant === "void"
          ? 0xb574ff
          : projectile.variant === "plasma"
            ? COLORS.coral
            : projectile.variant === "surge"
              ? COLORS.amber
              : COLORS.mint;
      group.add(new THREE.PointLight(color, 4.5, 4.2, 2));
    }
    return group;
  }

  private updateLegacyProjectile(
    object: THREE.Object3D,
    projectile: ProjectileState,
  ): void {
    const baseRadius: Record<ProjectileVariant, number> = {
      torpedo: 0.22,
      surge: 0.28,
      plasma: 0.3,
      void: 0.5,
    };
    object.position.set(projectile.position.x, 0.05, projectile.position.z);
    object.rotation.set(
      0,
      Math.atan2(projectile.velocity.x, projectile.velocity.z),
      projectile.variant === "surge" || projectile.variant === "void"
        ? this.elapsed * 5 + projectile.id
        : 0,
    );
    object.scale.setScalar(projectile.radius / baseRadius[projectile.variant]);
  }

  private syncProjectiles(projectiles: ProjectileState[]): void {
    if (this.projectileMode !== "final") {
      this.syncMap(
        projectiles,
        this.legacyProjectiles,
        (projectile) => this.buildLegacyProjectile(projectile),
        (object, projectile) => this.updateLegacyProjectile(object, projectile),
      );
      return;
    }
    if (!this.projectileMeshes) return;
    const counts: Record<ProjectileVariant, number> = {
      torpedo: 0,
      surge: 0,
      plasma: 0,
      void: 0,
    };
    const baseRadius: Record<ProjectileVariant, number> = {
      torpedo: 0.22,
      surge: 0.28,
      plasma: 0.3,
      void: 0.5,
    };
    for (const projectile of projectiles) {
      const index = counts[projectile.variant];
      if (index >= MAX_PROJECTILES) continue;
      const mesh = this.projectileMeshes[projectile.variant];
      const scale = projectile.radius / baseRadius[projectile.variant];
      this.projectileTransform.position.set(
        projectile.position.x,
        0.05,
        projectile.position.z,
      );
      this.projectileTransform.rotation.set(
        0,
        Math.atan2(projectile.velocity.x, projectile.velocity.z),
        projectile.variant === "surge" || projectile.variant === "void"
          ? this.elapsed * 5 + projectile.id
          : 0,
      );
      this.projectileTransform.scale.setScalar(scale);
      this.projectileTransform.updateMatrix();
      mesh.setMatrixAt(index, this.projectileTransform.matrix);
      counts[projectile.variant] += 1;
    }
    for (const variant of PROJECTILE_VARIANTS) {
      const mesh = this.projectileMeshes[variant];
      mesh.count = counts[variant];
      mesh.instanceMatrix.needsUpdate = true;
    }
  }

  private buildPickup(pickup: PickupState): THREE.Object3D {
    const group = new THREE.Group();
    const color = pickup.kind === "repair"
      ? COLORS.mint
      : pickup.kind === "shield"
        ? 0x8da9ff
        : COLORS.amber;
    const material = new THREE.MeshStandardMaterial({
      color,
      emissive: color,
      emissiveIntensity: pickup.kind === "surge" ? 3.1 : 1.8,
      roughness: 0.2,
      metalness: 0.25,
    });
    if (pickup.kind === "repair") {
      const core = new THREE.Mesh(new THREE.BoxGeometry(0.38, 0.38, 0.38), material);
      const horizontal = new THREE.Mesh(new THREE.BoxGeometry(1.15, 0.25, 0.25), material);
      const vertical = new THREE.Mesh(new THREE.BoxGeometry(0.25, 1.15, 0.25), material);
      group.add(core, horizontal, vertical);
    } else if (pickup.kind === "shield") {
      group.add(new THREE.Mesh(new THREE.IcosahedronGeometry(0.44, 1), material));
      for (const rotation of [0, Math.PI / 2]) {
        const ring = new THREE.Mesh(
          new THREE.TorusGeometry(0.66, 0.045, 6, 20),
          new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 0.78 }),
        );
        ring.rotation.x = rotation;
        group.add(ring);
      }
    } else if (pickup.kind === "score") {
      for (const [x, y, scale] of [[0, 0, 1], [-0.36, -0.1, 0.62], [0.36, -0.1, 0.62]] as const) {
        const crystal = new THREE.Mesh(new THREE.OctahedronGeometry(0.42, 0), material);
        crystal.position.set(x, y, 0);
        crystal.scale.setScalar(scale);
        group.add(crystal);
      }
    } else {
      const core = new THREE.Mesh(new THREE.OctahedronGeometry(0.55, 1), material);
      core.name = "surge-core";
      group.add(core);
      for (let index = 0; index < 3; index += 1) {
        const ring = new THREE.Mesh(
          new THREE.TorusGeometry(0.72 + index * 0.13, 0.045, 5, 22),
          new THREE.MeshBasicMaterial({
            color: index % 2 === 0 ? COLORS.amber : COLORS.mint,
            transparent: true,
            opacity: 0.78,
          }),
        );
        ring.rotation.x = index === 1 ? Math.PI / 2 : 0;
        ring.rotation.y = index === 2 ? Math.PI / 2 : 0;
        ring.name = "surge-ring";
        group.add(ring);
      }
      group.add(new THREE.PointLight(COLORS.amber, 16, 7, 2));
    }
    group.userData.pickupKind = pickup.kind;
    return group;
  }

  private updatePickup(object: THREE.Object3D, pickup: PickupState): void {
    object.position.set(
      pickup.position.x,
      0.25 + Math.sin(this.elapsed * 3 + pickup.id) * 0.22,
      pickup.position.z,
    );
    object.rotation.y += pickup.kind === "surge" ? 0.065 : 0.03;
    object.rotation.x = Math.sin(this.elapsed * 1.8 + pickup.id) * 0.18;
    const pulse = 1 + Math.sin(this.elapsed * (pickup.kind === "surge" ? 8 : 4) + pickup.id) * 0.1;
    object.scale.setScalar(pickup.kind === "surge" ? pulse * 1.18 : pulse);
    object.traverse((child) => {
      if (child.name === "surge-ring") child.rotation.z += 0.055;
    });
  }

  private handleEvents(events: GameEvent[], snapshot: GameSnapshot): void {
    for (const event of events) {
      if (event.type === "sonar_pulse") {
        this.addPulse(
          snapshot.player.position.x,
          snapshot.player.position.z,
          Number(event.data?.radius ?? 18),
          COLORS.mint,
          0.8,
        );
      }
      if (event.type === "boss_spawned") {
        this.addPulse(0, -14, 16, COLORS.coral, 1.5);
        this.cameraShake = Math.max(this.cameraShake, 0.72);
      }
      if (event.type === "player_damaged") {
        this.spawnSparks(snapshot.player.position.x, snapshot.player.position.z, COLORS.coral, 14);
        this.cameraShake = Math.max(this.cameraShake, 0.46);
      }
      if (event.type === "weapon_fired") {
        this.spawnSparks(
          Number(event.data?.x ?? snapshot.player.position.x),
          Number(event.data?.z ?? snapshot.player.position.z),
          Boolean(event.data?.overdrive) ? COLORS.amber : COLORS.mint,
          Boolean(event.data?.overdrive) ? 5 : 2,
        );
        this.cameraShake = Math.max(this.cameraShake, Boolean(event.data?.overdrive) ? 0.12 : 0.035);
      }
      if (event.type === "enemy_hit") {
        const x = Number(event.data?.x ?? 0);
        const z = Number(event.data?.z ?? 0);
        const object = this.enemies.get(Number(event.data?.id ?? -1));
        if (object) object.userData.hitUntil = this.elapsed + 0.095;
        this.spawnSparks(x, z, Boolean(event.data?.surge) ? COLORS.amber : COLORS.mint, 5);
        this.impactLight.position.set(x, 0.8, z);
        this.impactLight.color.setHex(Boolean(event.data?.surge) ? COLORS.amber : COLORS.mint);
        this.impactLight.intensity = Boolean(event.data?.surge) ? 24 : 14;
        this.cameraShake = Math.max(this.cameraShake, Boolean(event.data?.surge) ? 0.15 : 0.07);
      }
      if (event.type === "enemy_destroyed") {
        const x = Number(event.data?.x ?? 0);
        const z = Number(event.data?.z ?? 0);
        const boss = event.data?.enemyType === "leviathan";
        this.spawnSparks(x, z, boss ? COLORS.coral : COLORS.amber, boss ? 64 : 24);
        this.addPulse(x, z, boss ? 8 : 2.8, boss ? COLORS.coral : COLORS.amber, boss ? 1.2 : 0.42);
        this.cameraShake = Math.max(this.cameraShake, boss ? 1.15 : 0.32);
      }
      if (event.type === "pickup_collected") {
        const surge = event.data?.kind === "surge";
        this.addPulse(
          snapshot.player.position.x,
          snapshot.player.position.z,
          surge ? 9 : 3.2,
          surge ? COLORS.amber : COLORS.mint,
          surge ? 0.75 : 0.38,
        );
      }
      if (event.type === "overdrive_started") {
        const x = Number(event.data?.x ?? snapshot.player.position.x);
        const z = Number(event.data?.z ?? snapshot.player.position.z);
        this.addPulse(x, z, 14, COLORS.amber, 0.9);
        this.addPulse(x, z, 11, COLORS.mint, 0.72);
        this.spawnSparks(x, z, COLORS.amber, 42);
        this.cameraShake = Math.max(this.cameraShake, 0.82);
      }
    }
  }

  private addPulse(
    x: number,
    z: number,
    radius: number,
    color: number,
    duration: number,
  ): void {
    const material = new THREE.MeshBasicMaterial({
      color,
      transparent: true,
      opacity: 0.85,
      side: THREE.DoubleSide,
      depthWrite: false,
    });
    const mesh = new THREE.Mesh(new THREE.RingGeometry(0.85, 1, 64), material);
    mesh.rotation.x = -Math.PI / 2;
    mesh.position.set(x, -0.05, z);
    this.scene.add(mesh);
    this.pulses.push({ mesh, age: 0, duration, maxScale: radius });
  }

  private spawnSparks(x: number, z: number, color: number, count: number): void {
    const allowed = Math.max(0, Math.min(count, MAX_SPARKS - this.activeSparkCount));
    for (let index = 0; index < allowed; index += 1) {
      const spark = this.sparkPool[this.activeSparkCount];
      if (!spark) break;
      const angle = this.random.range(0, Math.PI * 2);
      const speed = this.random.range(1.2, 4.2);
      spark.position.set(x, 0, z);
      spark.velocity.set(
        Math.sin(angle) * speed,
        this.random.range(0.5, 2.6),
        Math.cos(angle) * speed,
      );
      spark.age = 0;
      spark.duration = this.random.range(0.35, 0.75);
      spark.color = color;
      this.activeSparkCount += 1;
    }
  }

  private animateWorld(delta: number, snapshot: GameSnapshot): void {
    this.cameraShake *= Math.pow(0.018, delta);
    if (this.cameraShake < 0.002) this.cameraShake = 0;
    this.impactLight.intensity *= Math.pow(0.002, delta);
    if (this.impactLight.intensity < 0.05) this.impactLight.intensity = 0;
    this.relay.rotation.y += delta * 0.28;
    const relaySignal = this.relay.children[2];
    if (relaySignal) relaySignal.scale.setScalar(1 + Math.sin(this.elapsed * 3) * 0.12);
    this.plankton.rotation.y += delta * 0.006;
    this.bubbles.position.y = ((this.elapsed * 0.22) % 8) - 3;
    if (this.targetReticle.visible) {
      const pulse = 1 + Math.sin(this.elapsed * 7) * 0.045;
      this.targetReticle.children[0]?.scale.setScalar(pulse);
    }

    const isMenu = snapshot.phase === "menu";
    if (isMenu) {
      this.player.rotation.y = Math.sin(this.elapsed * 0.22) * 0.5 + Math.PI;
      this.player.position.x = Math.sin(this.elapsed * 0.3) * 3.8;
      this.player.position.z = 4 + Math.cos(this.elapsed * 0.3) * 1.4;
    }

    for (let index = this.pulses.length - 1; index >= 0; index -= 1) {
      const pulse = this.pulses[index];
      if (!pulse) continue;
      pulse.age += delta;
      const progress = Math.min(1, pulse.age / pulse.duration);
      pulse.mesh.scale.setScalar(0.3 + pulse.maxScale * progress);
      const material = pulse.mesh.material as THREE.MeshBasicMaterial;
      material.opacity = (1 - progress) * 0.8;
      if (progress >= 1) {
        this.scene.remove(pulse.mesh);
        pulse.mesh.geometry.dispose();
        material.dispose();
        this.pulses.splice(index, 1);
      }
    }

    for (let index = this.activeSparkCount - 1; index >= 0; index -= 1) {
      const spark = this.sparkPool[index];
      if (!spark) continue;
      spark.age += delta;
      spark.velocity.y -= delta * 2.5;
      spark.position.addScaledVector(spark.velocity, delta);
      if (spark.age >= spark.duration) {
        const lastIndex = this.activeSparkCount - 1;
        const last = this.sparkPool[lastIndex];
        if (last && index !== lastIndex) {
          spark.position.copy(last.position);
          spark.velocity.copy(last.velocity);
          spark.age = last.age;
          spark.duration = last.duration;
          spark.color = last.color;
        }
        this.activeSparkCount -= 1;
      }
    }

    for (let index = 0; index < this.activeSparkCount; index += 1) {
      const spark = this.sparkPool[index];
      if (!spark) continue;
      this.sparkTransform.position.copy(spark.position);
      this.sparkTransform.rotation.set(0, 0, 0);
      this.sparkTransform.scale.setScalar(Math.max(0.01, 1 - spark.age / spark.duration));
      this.sparkTransform.updateMatrix();
      this.sparkMesh.setMatrixAt(index, this.sparkTransform.matrix);
      this.sparkMesh.setColorAt(index, this.sparkColor.setHex(spark.color));
    }
    this.sparkMesh.count = this.activeSparkCount;
    this.sparkMesh.instanceMatrix.needsUpdate = true;
    if (this.sparkMesh.instanceColor) this.sparkMesh.instanceColor.needsUpdate = true;
  }

  private resize = (): void => {
    const width = Math.max(1, window.innerWidth);
    const height = Math.max(1, window.innerHeight);
    this.camera.aspect = width / height;
    this.camera.fov = width < 720 ? 64 : 52;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(width, height, false);
  };
}
